/**
 * Advanced Chart Types for the BI Platform
 * Scatter (with quadrant + bubble-size), Combo (dual-axis), Gauge, Funnel,
 * Waterfall, Treemap (labelled), Heatmap, Sankey, Packed Bubbles, Geo Map.
 */
import React from 'react';
import { useExtensionConfig } from '@/hooks/useExtensionConfig';
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadialBarChart, RadialBar, Legend, FunnelChart, Funnel, LabelList, Cell,
  Treemap, ComposedChart, Bar, Line, Area, ZAxis, ReferenceLine, Sankey,
} from 'recharts';

const COLORS = ['#3b82f6', '#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];
const TT = { background: 'rgba(9,9,11,0.9)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, fontSize: 11, color: '#e2e8f0' };

// Default no-op formatter so charts work without `formatValue` being passed
// in by the caller. Callers like ChartRenderer DO pass it; this is a fallback.
const _identity = (v) => (v == null ? '' : Number.isFinite(+v) ? Number(+v).toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(v));

export function ScatterPlot({ data, xField, yField, sizeField, height = 340, formatValue, config = {} }) {
  // x + y are the two required measures. `sizeField` (optional 3rd measure)
  // drives the bubble radius — turns a scatter into a "bubble chart".
  // `config.showQuadrant`: split the canvas into 4 quadrants at the data
  // means with subtle reference lines so analysts can visually assess
  // "below average on X, above average on Y" segments.
  if (!data?.length || !xField || !yField) return null;
  const fmt = formatValue || _identity;
  const showQuadrant = config.showQuadrant !== false;
  const xVals = data.map(d => +d[xField]).filter(v => Number.isFinite(v));
  const yVals = data.map(d => +d[yField]).filter(v => Number.isFinite(v));
  const xMean = xVals.length ? xVals.reduce((a, b) => a + b, 0) / xVals.length : null;
  const yMean = yVals.length ? yVals.reduce((a, b) => a + b, 0) / yVals.length : null;
  const sizeVals = sizeField ? data.map(d => +d[sizeField]).filter(v => Number.isFinite(v)) : [];
  const sizeMax = sizeVals.length ? Math.max(...sizeVals) : 0;
  const sizeMin = sizeVals.length ? Math.min(...sizeVals) : 0;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ScatterChart margin={{ top: 16, right: 16, bottom: 8, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
        <XAxis type="number" dataKey={xField} tick={{ fontSize: 10, fill: '#a1a1aa' }} axisLine={false} name={xField} tickFormatter={(v) => fmt(v, xField)} />
        <YAxis type="number" dataKey={yField} tick={{ fontSize: 10, fill: '#a1a1aa' }} axisLine={false} name={yField} tickFormatter={(v) => fmt(v, yField)} />
        {sizeField && <ZAxis type="number" dataKey={sizeField} range={[40, 600]} name={sizeField} />}
        {showQuadrant && xMean != null && <ReferenceLine x={xMean} stroke="rgba(148,163,184,0.4)" strokeDasharray="4 4" />}
        {showQuadrant && yMean != null && <ReferenceLine y={yMean} stroke="rgba(148,163,184,0.4)" strokeDasharray="4 4" />}
        <Tooltip contentStyle={TT} cursor={{ strokeDasharray: '3 3', stroke: 'rgba(148,163,184,0.5)' }}
          formatter={(value, name) => [fmt(value, name), name]} />
        <Scatter data={data} fill="#3b82f6" fillOpacity={0.7} />
      </ScatterChart>
    </ResponsiveContainer>
  );
}

export function ComboChart({ data, xKey, barKeys = [], lineKeys = [], height = 340, showGrid = true, formatValue, config = {} }) {
  // Dual-axis combo: bars on the left Y, lines on the right Y when
  // `config.dualAxis` is true. Useful when measures have very different
  // scales (volume bars + ratio line, premium $ + loss ratio %, etc.).
  if (!data?.length) return null;
  const fmt = formatValue || _identity;
  const dualAxis = config.dualAxis !== false && barKeys.length && lineKeys.length;
  const leftLabel = config.showAxisTitles !== false ? (config.leftLabel || barKeys[0] || '') : '';
  const rightLabel = config.showAxisTitles !== false ? (config.rightLabel || lineKeys[0] || '') : '';
  const axisLabelStyle = { fontSize: 10, fill: '#a1a1aa', textAnchor: 'middle' };
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ top: 16, right: dualAxis ? 28 : 16, bottom: 8, left: 12 }}>
        {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />}
        <XAxis dataKey={xKey} tick={{ fontSize: 10, fill: '#a1a1aa' }} axisLine={false} />
        <YAxis yAxisId="left" tick={{ fontSize: 10, fill: '#a1a1aa' }} axisLine={false}
          tickFormatter={(v) => fmt(v, barKeys[0])} label={leftLabel ? { value: leftLabel, angle: -90, position: 'insideLeft', style: axisLabelStyle } : undefined} />
        {dualAxis && (
          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: '#a1a1aa' }} axisLine={false}
            tickFormatter={(v) => fmt(v, lineKeys[0])} label={rightLabel ? { value: rightLabel, angle: 90, position: 'insideRight', style: axisLabelStyle } : undefined} />
        )}
        <Tooltip contentStyle={TT} formatter={(value, name) => [fmt(value, name), name]} />
        <Legend wrapperStyle={{ fontSize: 10 }} />
        {barKeys.map((k, i) => <Bar key={k} yAxisId="left" dataKey={k} fill={COLORS[i % COLORS.length]} radius={[4, 4, 0, 0]} />)}
        {lineKeys.map((k, i) => <Line key={k} yAxisId={dualAxis ? 'right' : 'left'} type="monotone" dataKey={k} stroke={COLORS[(barKeys.length + i) % COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />)}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export function GaugeChart({ value = 0, max = 100, label = '', height = 200 }) {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  const data = [{ name: label || 'Value', value: pct, fill: pct > 80 ? '#ef4444' : pct > 60 ? '#f59e0b' : '#10b981' }];
  return (
    <ResponsiveContainer width="100%" height={height}>
      <RadialBarChart cx="50%" cy="50%" innerRadius="60%" outerRadius="90%" startAngle={180} endAngle={0} data={data}>
        <RadialBar background dataKey="value" cornerRadius={8} />
        <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" fontSize={24} fill="#e2e8f0" fontFamily="Manrope" fontWeight={700}>{value}</text>
        <text x="50%" y="62%" textAnchor="middle" fontSize={10} fill="#a1a1aa">{label}</text>
      </RadialBarChart>
    </ResponsiveContainer>
  );
}

export function FunnelChartViz({ data, nameKey = 'name', valueKey = 'value', height = 340 }) {
  if (!data?.length) return null;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <FunnelChart>
        <Tooltip contentStyle={TT} />
        <Funnel dataKey={valueKey} nameKey={nameKey} data={data} isAnimationActive>
          {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          <LabelList position="right" fill="#a1a1aa" fontSize={10} />
        </Funnel>
      </FunnelChart>
    </ResponsiveContainer>
  );
}

export function WaterfallChart({ data, xKey, valueKey, height = 340 }) {
  if (!data?.length) return null;
  // Convert to stacked bar format for waterfall effect
  let running = 0;
  const processed = data.map((d, i) => {
    const val = d[valueKey] || 0;
    const base = running;
    running += val;
    return { ...d, _base: Math.min(base, running), _top: Math.max(base, running), _val: val, _color: val >= 0 ? '#10b981' : '#ef4444' };
  });
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={processed}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
        <XAxis dataKey={xKey} tick={{ fontSize: 10, fill: '#a1a1aa' }} axisLine={false} />
        <YAxis tick={{ fontSize: 10, fill: '#a1a1aa' }} axisLine={false} />
        <Tooltip contentStyle={TT} />
        <Bar dataKey="_top" fill="transparent" stackId="waterfall" />
        <Bar dataKey="_val" stackId="waterfall" radius={[4, 4, 0, 0]}>
          {processed.map((d, i) => <Cell key={i} fill={d._color} />)}
        </Bar>
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export function TreemapChart({ data, nameKey = 'name', valueKey = 'value', height = 340, formatValue }) {
  if (!data?.length) return null;
  const fmt = formatValue || _identity;
  // Custom cell content so we can render the category name + value inside
  // every tile (Recharts' default Treemap labels are off by default).
  const ContentCell = (props) => {
    const { x, y, width, height: h, index, name, value } = props;
    if (width < 28 || h < 18) return null; // too small to label legibly
    const fill = COLORS[(index ?? 0) % COLORS.length];
    return (
      <g>
        <rect x={x} y={y} width={width} height={h} fill={fill} fillOpacity={0.85} stroke="rgba(0,0,0,0.25)" />
        <text x={x + 6} y={y + 14} fontSize={11} fill="#fff" fontWeight={600} pointerEvents="none">{String(name).substring(0, 24)}</text>
        {h > 32 && <text x={x + 6} y={y + 30} fontSize={10} fill="rgba(255,255,255,0.85)" pointerEvents="none">{fmt(value, valueKey)}</text>}
      </g>
    );
  };
  return (
    <ResponsiveContainer width="100%" height={height}>
      <Treemap data={data} dataKey={valueKey} nameKey={nameKey} content={<ContentCell />} isAnimationActive>
        <Tooltip contentStyle={TT} formatter={(value, name) => [fmt(value, valueKey), name]} />
      </Treemap>
    </ResponsiveContainer>
  );
}

/**
 * SankeyChart — flow diagram between two dims. `data` is an array of rows
 * each with the source dim, target dim, and a value measure. We build
 * the nodes (deduped) + links shape that Recharts' Sankey expects.
 *
 *   data:        [{source: 'A', target: 'X', value: 12}, ...]
 *   sourceKey:   field name for the source category
 *   targetKey:   field name for the target category
 *   valueKey:    field name for the flow magnitude
 */
export function SankeyChart({ data, sourceKey, targetKey, valueKey, height = 360, formatValue }) {
  // Marketplace extension: Sankey Plus (mkt_sankey_plus). When enabled it
  // can recolor links by source / target / value rather than the flat
  // default — gives flows visual weight without changing chart config.
  const sankeyPlus = useExtensionConfig('mkt_sankey_plus');
  const flowColorMode = sankeyPlus?.flow_color_mode || null;

  if (!data?.length || !sourceKey || !targetKey) return null;
  const fmt = formatValue || _identity;
  const srcNames = [...new Set(data.map(d => String(d[sourceKey] ?? '')))].filter(Boolean);
  const tgtNames = [...new Set(data.map(d => String(d[targetKey] ?? '')))].filter(Boolean);
  const seen = new Set();
  const nodes = [];
  for (const n of srcNames.concat(tgtNames)) {
    if (!seen.has(n)) { seen.add(n); nodes.push({ name: n }); }
  }
  const nameToIdx = Object.fromEntries(nodes.map((n, i) => [n.name, i]));
  const links = data
    .map(d => ({
      source: nameToIdx[String(d[sourceKey] ?? '')],
      target: nameToIdx[String(d[targetKey] ?? '')],
      value: Math.max(0, +d[valueKey] || 0),
    }))
    .filter(l => Number.isFinite(l.source) && Number.isFinite(l.target) && l.value > 0 && l.source !== l.target);

  if (!links.length) return <div className="text-xs text-muted-foreground p-4 text-center">No valid flows between {sourceKey} and {targetKey}.</div>;

  // Pre-compute the link color for the extension-driven modes.
  const maxValue = Math.max(...links.map(l => l.value));
  const sankeyPalette = ['#3b82f6', '#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];
  const linkColor = (link) => {
    if (flowColorMode === 'by_source') return sankeyPalette[link.source % sankeyPalette.length];
    if (flowColorMode === 'by_target') return sankeyPalette[link.target % sankeyPalette.length];
    if (flowColorMode === 'by_value') {
      // Heatmap: low → cool blue, high → warm red
      const ratio = link.value / (maxValue || 1);
      const r = Math.round(59 + (239 - 59) * ratio);
      const g = Math.round(130 - 60 * ratio);
      const b = Math.round(246 - 178 * ratio);
      return `rgba(${r}, ${g}, ${b}, 0.55)`;
    }
    return 'rgba(59,130,246,0.35)';
  };

  // Recharts' Sankey doesn't accept a per-link color via a function on the
  // top-level `link` prop, so we set each link's `stroke` inline.
  const linksWithColor = flowColorMode
    ? links.map(l => ({ ...l, stroke: linkColor(l) }))
    : links;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <Sankey
        data={{ nodes, links: linksWithColor }}
        nodeWidth={12}
        nodePadding={20}
        linkCurvature={0.5}
        iterations={64}
        link={flowColorMode
          ? (props) => {
              const c = props?.payload?.stroke || 'rgba(59,130,246,0.35)';
              const { sourceX, targetX, sourceY, targetY, sourceControlX, targetControlX, linkWidth } = props;
              return (
                <path
                  d={`M${sourceX},${sourceY}C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}`}
                  stroke={c}
                  strokeWidth={Math.max(1, linkWidth)}
                  fill="none"
                />
              );
            }
          : { stroke: 'rgba(59,130,246,0.35)' }}
        node={{ fill: '#3b82f6' }}
      >
        <Tooltip contentStyle={TT} formatter={(value, name) => [fmt(value, valueKey), name]} />
      </Sankey>
    </ResponsiveContainer>
  );
}

/**
 * PackedBubbleChart — non-overlapping bubble cluster sized by `valueKey`,
 * coloured by index of `nameKey`. Useful for "show me revenue share across
 * 30 categories" where a bar chart would be too tall. We use a simple
 * spiral-pack heuristic instead of pulling in d3-force to keep the bundle
 * lean — works well for 20–80 bubbles which is the typical use case.
 */
export function PackedBubbleChart({ data, nameKey = 'name', valueKey = 'value', height = 360, formatValue }) {
  if (!data?.length) return null;
  const fmt = formatValue || _identity;
  const W = 640, H = height;
  // Sort largest first so the big bubbles anchor the cluster center.
  const sorted = [...data]
    .map(d => ({ name: String(d[nameKey] ?? ''), value: +d[valueKey] || 0 }))
    .filter(d => d.value > 0)
    .sort((a, b) => b.value - a.value);
  if (!sorted.length) return null;
  // Radii proportional to sqrt(value) so AREA scales with the measure.
  const maxV = sorted[0].value;
  const scale = Math.min(W, H) * 0.18 / Math.sqrt(maxV);
  const placed = [];
  // Spiral packing: try angles around the current ring; expand outward
  // until a non-overlapping spot is found. Cheap and good-looking for ≤80.
  for (const d of sorted) {
    const r = Math.max(8, Math.sqrt(d.value) * scale);
    if (placed.length === 0) { placed.push({ ...d, x: W / 2, y: H / 2, r }); continue; }
    let found = false;
    for (let radius = r; radius < Math.max(W, H) && !found; radius += r * 0.6) {
      for (let a = 0; a < 360; a += 7) {
        const rad = (a * Math.PI) / 180;
        const x = W / 2 + Math.cos(rad) * radius;
        const y = H / 2 + Math.sin(rad) * radius;
        const overlap = placed.some(p => Math.hypot(p.x - x, p.y - y) < (p.r + r + 2));
        if (!overlap && x - r > 4 && x + r < W - 4 && y - r > 4 && y + r < H - 4) {
          placed.push({ ...d, x, y, r });
          found = true;
          break;
        }
      }
    }
    if (!found) placed.push({ ...d, x: W / 2, y: H / 2, r });
  }
  return (
    <div style={{ height }} data-testid="packed-bubble-chart">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={height} preserveAspectRatio="xMidYMid meet">
        {placed.map((b, i) => {
          const fill = COLORS[i % COLORS.length];
          return (
            <g key={i}>
              <circle cx={b.x} cy={b.y} r={b.r} fill={fill} fillOpacity={0.75} stroke={fill} strokeWidth={1}>
                <title>{`${b.name}: ${fmt(b.value, valueKey)}`}</title>
              </circle>
              {b.r > 18 && (
                <text x={b.x} y={b.y - 2} textAnchor="middle" fontSize={Math.min(13, b.r / 2.4)}
                  fill="#fff" fontWeight={600} pointerEvents="none">{b.name.substring(0, 12)}</text>
              )}
              {b.r > 28 && (
                <text x={b.x} y={b.y + 12} textAnchor="middle" fontSize={Math.min(11, b.r / 3)}
                  fill="rgba(255,255,255,0.85)" pointerEvents="none">{fmt(b.value, valueKey)}</text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export function HeatmapChart({ data, xKey, yKey, valueKey, height = 340 }) {
  if (!data?.length) return null;
  // Render as a colored grid using SVG
  const xVals = [...new Set(data.map(d => d[xKey]))];
  const yVals = [...new Set(data.map(d => d[yKey]))];
  const maxVal = Math.max(...data.map(d => Math.abs(d[valueKey] || 0)));
  const cellW = Math.max(30, Math.min(60, 500 / xVals.length));
  const cellH = Math.max(20, Math.min(40, height / yVals.length));
  const lookup = {};
  data.forEach(d => { lookup[`${d[xKey]}__${d[yKey]}`] = d[valueKey]; });

  return (
    <div style={{ height, overflowX: 'auto' }}>
      <svg width={xVals.length * cellW + 80} height={yVals.length * cellH + 30}>
        {/* X labels */}
        {xVals.map((x, xi) => <text key={`xl_${xi}`} x={80 + xi * cellW + cellW / 2} y={12} textAnchor="middle" fontSize={8} fill="#a1a1aa">{String(x).substring(0, 8)}</text>)}
        {/* Rows */}
        {yVals.map((y, yi) => (
          <g key={`yr_${yi}`}>
            <text x={75} y={25 + yi * cellH + cellH / 2} textAnchor="end" dominantBaseline="middle" fontSize={8} fill="#a1a1aa">{String(y).substring(0, 10)}</text>
            {xVals.map((x, xi) => {
              const val = lookup[`${x}__${y}`] || 0;
              const intensity = maxVal > 0 ? Math.abs(val) / maxVal : 0;
              const color = val >= 0 ? `rgba(59,130,246,${0.15 + intensity * 0.85})` : `rgba(239,68,68,${0.15 + intensity * 0.85})`;
              return <rect key={`c_${xi}_${yi}`} x={80 + xi * cellW} y={20 + yi * cellH} width={cellW - 1} height={cellH - 1} rx={3} fill={color}><title>{`${x}, ${y}: ${val}`}</title></rect>;
            })}
          </g>
        ))}
      </svg>
    </div>
  );
}

// ─── Geographic Map ───
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
import { resolveCity as _resolveCity } from '@/lib/usCities';

const US_GEO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';
const US_COUNTIES_GEO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/counties-10m.json';
const WORLD_GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

// US state FIPS → 2-letter abbreviation. Used when the TopoJSON geo `id`
// comes back as a FIPS code rather than a name.
const FIPS_TO_ABBR = {"01":"AL","02":"AK","04":"AZ","05":"AR","06":"CA","08":"CO","09":"CT","10":"DE","11":"DC","12":"FL","13":"GA","15":"HI","16":"ID","17":"IL","18":"IN","19":"IA","20":"KS","21":"KY","22":"LA","23":"ME","24":"MD","25":"MA","26":"MI","27":"MN","28":"MS","29":"MO","30":"MT","31":"NE","32":"NV","33":"NH","34":"NJ","35":"NM","36":"NY","37":"NC","38":"ND","39":"OH","40":"OK","41":"OR","42":"PA","44":"RI","45":"SC","46":"SD","47":"TN","48":"TX","49":"UT","50":"VT","51":"VA","53":"WA","54":"WV","55":"WI","56":"WY"};
const STATE_NAME_TO_ABBR = {"Alabama":"AL","Alaska":"AK","Arizona":"AZ","Arkansas":"AR","California":"CA","Colorado":"CO","Connecticut":"CT","Delaware":"DE","District of Columbia":"DC","Florida":"FL","Georgia":"GA","Hawaii":"HI","Idaho":"ID","Illinois":"IL","Indiana":"IN","Iowa":"IA","Kansas":"KS","Kentucky":"KY","Louisiana":"LA","Maine":"ME","Maryland":"MD","Massachusetts":"MA","Michigan":"MI","Minnesota":"MN","Mississippi":"MS","Missouri":"MO","Montana":"MT","Nebraska":"NE","Nevada":"NV","New Hampshire":"NH","New Jersey":"NJ","New Mexico":"NM","New York":"NY","North Carolina":"NC","North Dakota":"ND","Ohio":"OH","Oklahoma":"OK","Oregon":"OR","Pennsylvania":"PA","Rhode Island":"RI","South Carolina":"SC","South Dakota":"SD","Tennessee":"TN","Texas":"TX","Utah":"UT","Vermont":"VT","Virginia":"VA","Washington":"WA","West Virginia":"WV","Wisconsin":"WI","Wyoming":"WY"};

// Top ISO codes ↔ country-name lookups (world-atlas TopoJSON uses ISO numeric
// for geo.id and the English name in geo.properties.name). We support all
// three forms (numeric / alpha-2 / alpha-3) plus the name itself when
// matching against user data. Covers the ~80 countries that ~99% of BI
// dashboards reference; the long tail still falls back to name match.
const COUNTRY_LOOKUP = {
  // [iso_numeric]: [alpha2, alpha3, name]
  "004":["AF","AFG","Afghanistan"],"008":["AL","ALB","Albania"],"012":["DZ","DZA","Algeria"],
  "032":["AR","ARG","Argentina"],"036":["AU","AUS","Australia"],"040":["AT","AUT","Austria"],
  "048":["BH","BHR","Bahrain"],"050":["BD","BGD","Bangladesh"],"056":["BE","BEL","Belgium"],
  "068":["BO","BOL","Bolivia"],"076":["BR","BRA","Brazil"],"100":["BG","BGR","Bulgaria"],
  "124":["CA","CAN","Canada"],"152":["CL","CHL","Chile"],"156":["CN","CHN","China"],
  "170":["CO","COL","Colombia"],"188":["CR","CRI","Costa Rica"],"191":["HR","HRV","Croatia"],
  "192":["CU","CUB","Cuba"],"196":["CY","CYP","Cyprus"],"203":["CZ","CZE","Czechia"],
  "208":["DK","DNK","Denmark"],"214":["DO","DOM","Dominican Republic"],"218":["EC","ECU","Ecuador"],
  "818":["EG","EGY","Egypt"],"222":["SV","SLV","El Salvador"],"233":["EE","EST","Estonia"],
  "246":["FI","FIN","Finland"],"250":["FR","FRA","France"],"276":["DE","DEU","Germany"],
  "300":["GR","GRC","Greece"],"320":["GT","GTM","Guatemala"],"344":["HK","HKG","Hong Kong"],
  "348":["HU","HUN","Hungary"],"352":["IS","ISL","Iceland"],"356":["IN","IND","India"],
  "360":["ID","IDN","Indonesia"],"364":["IR","IRN","Iran"],"368":["IQ","IRQ","Iraq"],
  "372":["IE","IRL","Ireland"],"376":["IL","ISR","Israel"],"380":["IT","ITA","Italy"],
  "388":["JM","JAM","Jamaica"],"392":["JP","JPN","Japan"],"400":["JO","JOR","Jordan"],
  "404":["KE","KEN","Kenya"],"410":["KR","KOR","South Korea"],"414":["KW","KWT","Kuwait"],
  "428":["LV","LVA","Latvia"],"422":["LB","LBN","Lebanon"],"434":["LY","LBY","Libya"],
  "440":["LT","LTU","Lithuania"],"442":["LU","LUX","Luxembourg"],"458":["MY","MYS","Malaysia"],
  "484":["MX","MEX","Mexico"],"504":["MA","MAR","Morocco"],"528":["NL","NLD","Netherlands"],
  "554":["NZ","NZL","New Zealand"],"566":["NG","NGA","Nigeria"],"578":["NO","NOR","Norway"],
  "586":["PK","PAK","Pakistan"],"591":["PA","PAN","Panama"],"604":["PE","PER","Peru"],
  "608":["PH","PHL","Philippines"],"616":["PL","POL","Poland"],"620":["PT","PRT","Portugal"],
  "634":["QA","QAT","Qatar"],"642":["RO","ROU","Romania"],"643":["RU","RUS","Russia"],
  "682":["SA","SAU","Saudi Arabia"],"702":["SG","SGP","Singapore"],"703":["SK","SVK","Slovakia"],
  "705":["SI","SVN","Slovenia"],"710":["ZA","ZAF","South Africa"],"724":["ES","ESP","Spain"],
  "752":["SE","SWE","Sweden"],"756":["CH","CHE","Switzerland"],"764":["TH","THA","Thailand"],
  "792":["TR","TUR","Turkey"],"804":["UA","UKR","Ukraine"],"784":["AE","ARE","United Arab Emirates"],
  "826":["GB","GBR","United Kingdom"],"840":["US","USA","United States"],
  "858":["UY","URY","Uruguay"],"862":["VE","VEN","Venezuela"],"704":["VN","VNM","Vietnam"],
};

// Approximate country centroids [lat, lng] keyed by ISO numeric (matches
// COUNTRY_LOOKUP) — used to place point / heat-map overlays for world data.
const COUNTRY_CENTROIDS_BY_ISO = {
  "004":[33,66],"008":[41,20],"012":[28,3],"032":[-34,-64],"036":[-25,133],"040":[47.5,14.5],
  "048":[26,50.5],"050":[24,90],"056":[50.6,4.6],"068":[-17,-65],"076":[-10,-53],"100":[42.7,25.5],
  "124":[60,-96],"152":[-35,-71],"156":[35,105],"170":[4,-73],"188":[10,-84],"191":[45.1,15.5],
  "192":[21.5,-79.5],"196":[35,33],"203":[49.8,15.5],"208":[56,10],"214":[19,-70.7],"218":[-1.5,-78],
  "818":[27,30],"222":[13.8,-88.9],"233":[59,26],"246":[64,26],"250":[46.6,2.2],"276":[51,10.4],
  "300":[39,22],"320":[15.5,-90.3],"344":[22.3,114.2],"348":[47,19.5],"352":[65,-18],"356":[22,79],
  "360":[-2,118],"364":[32,53],"368":[33,44],"372":[53.2,-8],"376":[31,35],"380":[42.8,12.8],
  "388":[18.1,-77.3],"392":[36,138],"400":[31,36],"404":[0,38],"410":[36.5,127.8],"414":[29.3,47.6],
  "428":[57,25],"422":[33.9,35.9],"434":[27,17],"440":[55.2,23.9],"442":[49.8,6.1],"458":[4,109.5],
  "484":[23,-102],"504":[32,-6],"528":[52.2,5.3],"554":[-42,172],"566":[9,8],"578":[62,10],
  "586":[30,70],"591":[8.5,-80],"604":[-10,-76],"608":[13,122],"616":[52,19],"620":[39.5,-8],
  "634":[25.3,51.2],"642":[46,25],"643":[61,100],"682":[24,45],"702":[1.35,103.8],"703":[48.7,19.5],
  "705":[46.1,14.8],"710":[-29,24],"724":[40,-4],"752":[62,15],"756":[46.8,8.2],"764":[15,101],
  "792":[39,35],"804":[49,32],"784":[24,54],"826":[54,-2],"840":[39.5,-98.5],"858":[-33,-56],
  "862":[7,-66],"704":[16,108],
};

const STATE_COORDS = {"AL":[32.8,-86.8],"AK":[64,-153],"AZ":[34.3,-111.7],"AR":[34.8,-92.2],"CA":[37,-119.5],"CO":[39,-105.5],"CT":[41.6,-72.7],"DE":[39,-75.5],"FL":[28.6,-82.4],"GA":[33,-83.5],"HI":[20.8,-156.3],"ID":[44.4,-114.6],"IL":[40,-89.2],"IN":[40,-86.3],"IA":[42,-93.5],"KS":[38.5,-98.4],"KY":[37.8,-85.7],"LA":[31,-92],"ME":[45.4,-69],"MD":[39.3,-76.6],"MA":[42.2,-71.5],"MI":[44.3,-84.5],"MN":[46.3,-94.3],"MS":[32.7,-89.7],"MO":[38.4,-92.5],"MT":[47,-109.6],"NE":[41.5,-99.8],"NV":[39.5,-116.9],"NH":[43.7,-71.5],"NJ":[40.2,-74.7],"NM":[34.5,-106],"NY":[42.9,-75.5],"NC":[35.5,-79.4],"ND":[47.4,-100.5],"OH":[40.2,-82.8],"OK":[35.5,-97.5],"OR":[44,-120.5],"PA":[40.9,-77.8],"RI":[41.7,-71.5],"SC":[33.8,-80.9],"SD":[44.4,-100.2],"TN":[35.8,-86.4],"TX":[31.5,-99.4],"UT":[39.3,-111.7],"VT":[44,-72.7],"VA":[37.5,-78.8],"WA":[47.4,-120.5],"WV":[38.6,-80.6],"WI":[44.6,-89.8],"WY":[43,-107.5],"DC":[38.9,-77]};

// ── Helpers ─────────────────────────────────────────────────────────────

/**
 * Detect whether the user's data column holds US states or world countries.
 * We probe the first ~20 values, normalize, and check against our lookups.
 * Returns 'us-states' | 'world' | null (caller decides on a fallback).
 */
function _detectGeoScope(rows, locationKey) {
  if (!rows?.length || !locationKey) return null;
  let usHits = 0;
  let worldHits = 0;
  const seen = new Set();
  for (const r of rows.slice(0, 30)) {
    const v = r[locationKey];
    if (v === null || v === undefined) continue;
    const norm = String(v).trim();
    if (!norm || seen.has(norm)) continue;
    seen.add(norm);
    const upper = norm.toUpperCase();
    if (upper in STATE_COORDS || norm in STATE_NAME_TO_ABBR) usHits++;
    else if (_findCountry(norm)) worldHits++;
  }
  if (usHits === 0 && worldHits === 0) return null;
  return worldHits > usHits ? 'world' : 'us-states';
}

/**
 * Look up a country in `COUNTRY_LOOKUP` by alpha-2 / alpha-3 / name.
 * Returns the ISO-numeric key (matches world-atlas geo.id) or null.
 */
function _findCountry(rawValue) {
  if (!rawValue) return null;
  const s = String(rawValue).trim();
  const upper = s.toUpperCase();
  for (const [iso, [a2, a3, name]] of Object.entries(COUNTRY_LOOKUP)) {
    if (upper === a2 || upper === a3 || name.toLowerCase() === s.toLowerCase()) {
      return iso;
    }
  }
  return null;
}

// ── US County matching (FIPS + name/state) ─────────────────────────────
// us-atlas counties give feature.id = 5-digit county FIPS and
// properties.name = county name only. The state is derived from the FIPS
// prefix (first 2 digits), which lets us resolve BOTH raw FIPS codes and
// "County, ST"/"County, State"/plain-name inputs from user data.
const _normCountyName = (s) => String(s || '').toUpperCase()
  .replace(/[.'`]/g, '')
  .replace(/\b(COUNTY|PARISH|BOROUGH|CENSUS AREA|MUNICIPALITY|CITY AND BOROUGH)\b/g, '')
  .replace(/\bSAINT\b/g, 'ST')
  .replace(/[^A-Z0-9 ]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

// Build (and cache on the boundaries object) fips + name/state indexes.
function _countyIndex(boundaries) {
  if (!boundaries) return null;
  if (boundaries.__countyIndex) return boundaries.__countyIndex;
  const byFips = {};
  const byNameState = {};
  const nameCounts = {};
  const byName = {};
  for (const f of (boundaries.features || [])) {
    const fips = String(f.id).padStart(5, '0');
    byFips[fips] = true;
    const nm = _normCountyName(f.properties && f.properties.name);
    const st = FIPS_TO_ABBR[fips.slice(0, 2)];
    if (nm) {
      if (st) byNameState[`${st}|${nm}`] = fips;
      nameCounts[nm] = (nameCounts[nm] || 0) + 1;
      byName[nm] = fips;
    }
  }
  const idx = { byFips, byNameState, byName, nameCounts };
  try { Object.defineProperty(boundaries, '__countyIndex', { value: idx, enumerable: false }); }
  catch { /* noop */ }
  return idx;
}

// Resolve a user county value → 5-digit FIPS using the boundary index.
function _resolveCountyFips(raw, idx) {
  if (raw == null || !idx) return null;
  const s = String(raw).trim();
  if (!s) return null;
  const bare = s.replace(/\s+/g, '');
  if (/^\d{4,5}$/.test(bare)) {
    const f = bare.padStart(5, '0');
    if (idx.byFips[f]) return f;
  }
  // "Name, ST"  (2-letter state)
  let m = s.match(/^(.*?)[,\-|]\s*([A-Za-z]{2})$/);
  if (m) {
    const key = `${m[2].toUpperCase()}|${_normCountyName(m[1])}`;
    if (idx.byNameState[key]) return idx.byNameState[key];
  }
  // "Name, StateFullName"
  m = s.match(/^(.*?),\s*(.+)$/);
  if (m) {
    const st = STATE_NAME_TO_ABBR[m[2].trim()];
    if (st) {
      const key = `${st}|${_normCountyName(m[1])}`;
      if (idx.byNameState[key]) return idx.byNameState[key];
    }
  }
  // Plain county name — only accept when unambiguous nationwide.
  const nmOnly = _normCountyName(s);
  if (idx.nameCounts[nmOnly] === 1) return idx.byName[nmOnly];
  return null;
}

/**
 * 7-stop diverging color scale (red → white → blue) for choropleths.
 * Returns a fill color for `val` given the data range. When `divergeAt`
 * is null we use a sequential blue scale; otherwise the scale pivots at
 * `divergeAt` so negative deltas turn red and positives turn blue.
 */
function _colorForValue(val, min, max, divergeAt) {
  if (val == null || isNaN(val) || min === max) return 'rgba(148,163,184,0.18)';
  if (divergeAt != null) {
    // Diverging — useful for "deltas vs benchmark" maps.
    const range = Math.max(Math.abs(divergeAt - min), Math.abs(max - divergeAt));
    const t = (val - divergeAt) / range; // in [-1, 1]
    if (t < 0) {
      const a = Math.min(1, Math.abs(t));
      return `rgba(239,68,68,${0.15 + a * 0.7})`;
    }
    const a = Math.min(1, t);
    return `rgba(59,130,246,${0.15 + a * 0.7})`;
  }
  const t = (val - min) / (max - min);
  return `rgba(59,130,246,${0.15 + t * 0.8})`;
}

/**
 * Build N quantile breakpoints from a sorted numeric array. Used for the
 * legend so the swatches reflect the actual data distribution instead of
 * a naive equal-step gradient.
 */
function _quantiles(values, n = 5) {
  if (!values.length) return [];
  const sorted = [...values].sort((a, b) => a - b);
  const out = [sorted[0]];
  for (let i = 1; i < n; i++) {
    const q = sorted[Math.floor((i / n) * sorted.length)];
    if (q !== out[out.length - 1]) out.push(q);
  }
  out.push(sorted[sorted.length - 1]);
  return out;
}

export function GeoMapChart({ data, locationKey, valueKey, height = 400, config = {}, formatValue, onHover }) {
  // `data`: array of { [locationKey]: 'CA'|'California'|'US'|'United States', [valueKey]: number }
  // `config.geoScope`: 'us-states' | 'world' | 'auto'  (default: 'auto')
  // `config.geoMode`:  'choropleth' | 'points'         (default: 'choropleth')
  // `config.divergeAt`: optional pivot value for the diverging color scale.
  // `formatValue(v, fieldName)`: optional unit-aware formatter (passed
  //   through from ChartRenderer).
  const [hover, setHover] = React.useState(null); // { name, value, x, y } | null
  const wrapRef = React.useRef(null);
  // Cursor position RELATIVE to the map container — robust even when a
  // transformed/animated ancestor (e.g. an AI Explore message card) would
  // otherwise break `position: fixed` viewport coordinates.
  const _rel = (evt) => {
    const r = wrapRef.current?.getBoundingClientRect();
    return r ? { x: evt.clientX - r.left, y: evt.clientY - r.top } : { x: evt.clientX, y: evt.clientY };
  };
  if (!data?.length) return null;

  const mode = config.geoMode || 'choropleth';
  const requestedScope = config.geoScope || 'auto';
  const scope = requestedScope === 'auto'
    ? (_detectGeoScope(data, locationKey) || 'us-states')
    : requestedScope;

  // Build value lookup keyed by the canonical region identifier for the
  // chosen scope. For US: 2-letter abbreviation. For World: ISO numeric.
  const lookup = {};
  const numericValues = [];
  for (const d of data) {
    const raw = d[locationKey];
    if (raw == null) continue;
    let key = null;
    if (scope === 'us-states') {
      const s = String(raw).trim();
      key = STATE_NAME_TO_ABBR[s] || s.toUpperCase();
    } else {
      key = _findCountry(raw);
    }
    if (!key) continue;
    const v = Number(d[valueKey]);
    if (!isNaN(v)) {
      lookup[key] = { value: v, raw: d[valueKey], original: raw };
      numericValues.push(v);
    }
  }

  const minV = numericValues.length ? Math.min(...numericValues) : 0;
  const maxV = numericValues.length ? Math.max(...numericValues) : 1;
  const divergeAt = config.divergeAt ?? null;
  const projection = scope === 'world' ? 'geoEqualEarth' : 'geoAlbersUsa';
  const geoUrl = scope === 'world' ? WORLD_GEO_URL : US_GEO_URL;
  const fmt = (v) => {
    if (typeof formatValue === 'function') {
      try { return formatValue(v, valueKey); } catch { /* fall through */ }
    }
    if (v == null || isNaN(v)) return '—';
    return Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  // 5-step legend swatches — pulled from data quantiles so the gradient
  // actually reflects the distribution rather than a linear ramp.
  const legendStops = _quantiles(numericValues, 5);

  const getRegionKey = (geo) => {
    if (scope === 'us-states') {
      return FIPS_TO_ABBR[geo.id] || STATE_NAME_TO_ABBR[geo.properties?.name] || null;
    }
    return geo.id || null;
  };
  const getRegionName = (geo) => {
    if (scope === 'us-states') return geo.properties?.name || FIPS_TO_ABBR[geo.id] || '—';
    return geo.properties?.name || COUNTRY_LOOKUP[geo.id]?.[2] || '—';
  };

  return (
    <div ref={wrapRef} style={{ height, position: 'relative' }} data-testid="geo-map-chart">
      <ComposableMap
        projection={projection}
        projectionConfig={scope === 'world' ? { scale: 150 } : undefined}
        style={{ width: '100%', height: '100%' }}
      >
        <ZoomableGroup>
          <Geographies geography={geoUrl}>
            {({ geographies }) => geographies.map(geo => {
              const key = getRegionKey(geo);
              const entry = key ? lookup[key] : null;
              const fill = entry
                ? (mode === 'choropleth' ? _colorForValue(entry.value, minV, maxV, divergeAt) : 'rgba(148,163,184,0.10)')
                : 'rgba(148,163,184,0.08)';
              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={fill}
                  stroke="rgba(255,255,255,0.18)"
                  strokeWidth={0.5}
                  style={{
                    default: { outline: 'none', transition: 'fill 120ms' },
                    hover:   { outline: 'none', fill: entry ? '#f59e0b' : 'rgba(148,163,184,0.18)', cursor: 'pointer' },
                    pressed: { outline: 'none' },
                  }}
                  onMouseEnter={(evt) => { const nm = getRegionName(geo); const p = _rel(evt); setHover({ name: nm, value: entry ? entry.value : null, x: p.x, y: p.y }); if (onHover) onHover({ [locationKey]: nm, [valueKey]: entry ? entry.value : null }); }}
                  onMouseMove={(evt) => { const p = _rel(evt); setHover(prev => prev && { ...prev, x: p.x, y: p.y }); }}
                  onMouseLeave={() => setHover(null)}
                />
              );
            })}
          </Geographies>
          {mode === 'points' && scope === 'us-states' && data.map((d, i) => {
            const loc = String(d[locationKey] || '').toUpperCase();
            const abbr = STATE_NAME_TO_ABBR[String(d[locationKey])] || loc;
            const coords = STATE_COORDS[abbr];
            if (!coords) return null;
            const val = Number(d[valueKey]) || 0;
            const r = Math.max(3, Math.min(20, (val / (maxV || 1)) * 18));
            return (
              <Marker key={i} coordinates={[coords[1], coords[0]]}>
                <circle r={r} fill="rgba(59,130,246,0.7)" stroke="#3b82f6" strokeWidth={1}
                  onMouseEnter={(evt) => { const p = _rel(evt); setHover({ name: abbr, value: val, x: p.x, y: p.y }); }}
                  onMouseLeave={() => setHover(null)} />
              </Marker>
            );
          })}
        </ZoomableGroup>
      </ComposableMap>

      {/* Floating tooltip — follows the cursor and uses the formatter so
          units render correctly (e.g. "California: 68.9%" or "Germany: $1.2M"). */}
      {hover && (
        <div
          style={{
            position: 'absolute', left: hover.x + 12, top: hover.y + 12, zIndex: 50,
            pointerEvents: 'none',
            ...TT, padding: '6px 10px', whiteSpace: 'nowrap',
          }}
          data-testid="geo-map-tooltip"
        >
          <div style={{ fontWeight: 600 }}>{hover.name}</div>
          <div style={{ color: '#cbd5e1' }}>{hover.value == null ? 'no data' : fmt(hover.value)}</div>
        </div>
      )}

      {/* Legend — 5 quantile swatches with min/max labels. Hidden for
          point-only maps since size already encodes the value. */}
      {mode === 'choropleth' && legendStops.length > 1 && (
        <div style={{
          position: 'absolute', bottom: 8, left: 12, right: 12,
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 10, color: '#94a3b8',
        }} data-testid="geo-map-legend">
          <span>{fmt(legendStops[0])}</span>
          <div style={{ flex: 1, height: 8, borderRadius: 4, background: 'linear-gradient(90deg, rgba(59,130,246,0.15), rgba(59,130,246,0.95))' }} />
          <span>{fmt(legendStops[legendStops.length - 1])}</span>
        </div>
      )}
    </div>
  );
}

// ─── MapLibre basemap geo chart (satellite / terrain / streets + points / heatmap) ───
// Raster basemaps from free, no-API-key tile providers. Attribution required.
const MAPLIBRE_ATTR = {
  streets: '© OpenStreetMap contributors',
  satellite: 'Powered by Esri — Source: Esri, Maxar, Earthstar Geographics',
  terrain: 'Map data © OpenStreetMap contributors, SRTM · style © OpenTopoMap (CC-BY-SA)',
};
const MAPLIBRE_BASEMAPS = {
  streets: {
    version: 8,
    sources: { base: { type: 'raster', tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'], tileSize: 256, attribution: MAPLIBRE_ATTR.streets } },
    layers: [{ id: 'base', type: 'raster', source: 'base' }],
  },
  satellite: {
    version: 8,
    sources: { base: { type: 'raster', tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'], tileSize: 256, attribution: MAPLIBRE_ATTR.satellite } },
    layers: [{ id: 'base', type: 'raster', source: 'base' }],
  },
  terrain: {
    version: 8,
    sources: { base: { type: 'raster', tiles: ['https://a.tile.opentopomap.org/{z}/{x}/{y}.png', 'https://b.tile.opentopomap.org/{z}/{x}/{y}.png', 'https://c.tile.opentopomap.org/{z}/{x}/{y}.png'], tileSize: 256, attribution: MAPLIBRE_ATTR.terrain } },
    layers: [{ id: 'base', type: 'raster', source: 'base' }],
  },
};
const MAPLIBRE_VIEW = {
  'us-states': { center: [-98.5, 39.5], zoom: 3.1 },
  'us-counties': { center: [-98.5, 39.5], zoom: 3.1 },
  'us-cities': { center: [-98.5, 39.5], zoom: 3.1 },
  world: { center: [10, 25], zoom: 1.1 },
};
const _escapeHtml = (v) => String(v == null ? '' : v).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
// Flatten any GeoJSON geometry's coordinates into a flat list of [lng,lat] pairs.
const _collectPolyCoords = (geom, out) => {
  if (!geom || !geom.coordinates) return;
  const walk = (arr) => { if (typeof arr[0] === 'number') { out.push(arr); } else { arr.forEach(walk); } };
  walk(geom.coordinates);
};
// Load + cache region boundary GeoJSON (once per scope). topojson-client is a
// CJS module — under webpack `import()` its `feature` fn may sit on `.default`.
const _boundaryCache = {};
async function _loadBoundaries(scope) {
  if (_boundaryCache[scope]) return _boundaryCache[scope];
  const tj = await import('topojson-client');
  const feature = tj.feature || (tj.default && tj.default.feature);
  const url = scope === 'world' ? WORLD_GEO_URL : scope === 'us-counties' ? US_COUNTIES_GEO_URL : US_GEO_URL;
  const topo = await (await fetch(url)).json();
  const objKey = scope === 'world' ? 'countries' : scope === 'us-counties' ? 'counties' : 'states';
  const objName = topo.objects && topo.objects[objKey] ? objKey : Object.keys(topo.objects || {})[0];
  const gj = feature(topo, topo.objects[objName]);
  _boundaryCache[scope] = gj;
  return gj;
}

export function MapLibreGeoChart({ data, locationKey, valueKey, height = 400, config = {}, formatValue }) {
  const containerRef = React.useRef(null);
  const mapRef = React.useRef(null);
  const readyRef = React.useRef(false);
  const mode = ['choropleth', 'points', 'heatmap'].includes(config.geoMode) ? config.geoMode : 'choropleth';
  const basemap = MAPLIBRE_BASEMAPS[config.basemap] ? config.basemap : 'streets';
  const scope = (config.geoScope && config.geoScope !== 'auto')
    ? config.geoScope
    : (_detectGeoScope(data, locationKey) || 'us-states');
  const fmt = (v) => {
    try { return typeof formatValue === 'function' ? formatValue(v, valueKey) : Number(v).toLocaleString(); }
    catch { return String(v); }
  };

  // Build a GeoJSON FeatureCollection from region rows using known centroids.
  const fc = React.useMemo(() => {
    const features = [];
    for (const d of (data || [])) {
      const raw = d[locationKey];
      const v = Number(d[valueKey]);
      if (raw == null || !Number.isFinite(v)) continue;
      let lat, lng;
      if (scope === 'us-cities') {
        const c = _resolveCity(raw);
        if (!c) continue;
        lat = c[0]; lng = c[1];
      } else if (scope === 'us-states') {
        const abbr = STATE_NAME_TO_ABBR[String(raw).trim()] || String(raw).trim().toUpperCase();
        const c = STATE_COORDS[abbr];
        if (!c) continue;
        lat = c[0]; lng = c[1];
      } else {
        const iso = _findCountry(raw);
        const c = iso ? COUNTRY_CENTROIDS_BY_ISO[iso] : null;
        if (!c) continue;
        lat = c[0]; lng = c[1];
      }
      features.push({ type: 'Feature', geometry: { type: 'Point', coordinates: [lng, lat] }, properties: { name: String(raw), value: Math.max(0, v) } });
    }
    return { type: 'FeatureCollection', features };
  }, [data, locationKey, valueKey, scope]);
  const maxV = React.useMemo(() => fc.features.reduce((m, f) => Math.max(m, f.properties.value), 0) || 1, [fc]);

  // Choropleth polygons — load boundary geometry ONCE per scope (cached), then
  // memoize the value-join. This decouples the async fetch from the sync join so
  // there's no race that can leave the source empty.
  const [boundaries, setBoundaries] = React.useState(null);
  React.useEffect(() => {
    if (mode !== 'choropleth') { setBoundaries(null); return undefined; }
    let cancelled = false;
    _loadBoundaries(scope).then(gj => { if (!cancelled) setBoundaries(gj); }).catch(() => { if (!cancelled) setBoundaries({ type: 'FeatureCollection', features: [] }); });
    return () => { cancelled = true; };
  }, [mode, scope]);

  const regionFC = React.useMemo(() => {
    if (mode !== 'choropleth' || !boundaries) return null;
    const idx = scope === 'us-counties' ? _countyIndex(boundaries) : null;
    const lookup = {};
    const nums = [];
    for (const d of (data || [])) {
      const raw = d[locationKey]; const v = Number(d[valueKey]);
      if (raw == null || !Number.isFinite(v)) continue;
      const key = scope === 'world'
        ? _findCountry(raw)
        : scope === 'us-counties'
          ? _resolveCountyFips(raw, idx)
          : (STATE_NAME_TO_ABBR[String(raw).trim()] || String(raw).trim().toUpperCase());
      if (!key) continue;
      lookup[key] = { value: v, label: String(raw) }; nums.push(v);
    }
    const min = nums.length ? Math.min(...nums) : 0;
    const max = nums.length ? Math.max(...nums) : 1;
    const div = config.divergeAt ?? null;
    const features = boundaries.features.map(f => {
      const key = scope === 'world'
        ? String(f.id)
        : scope === 'us-counties'
          ? String(f.id).padStart(5, '0')
          : (FIPS_TO_ABBR[f.id] || (f.properties && STATE_NAME_TO_ABBR[f.properties.name]) || null);
      const entry = key ? lookup[key] : null;
      return {
        type: 'Feature', id: f.id, geometry: f.geometry,
        properties: {
          name: (f.properties && f.properties.name) || '',
          _val: entry ? entry.value : null,
          _label: entry ? entry.label : ((f.properties && f.properties.name) || ''),
          _fill: entry ? _colorForValue(entry.value, min, max, div) : 'rgba(148,163,184,0.12)',
        },
      };
    });
    return { type: 'FeatureCollection', features };
  }, [boundaries, mode, scope, data, locationKey, valueKey, config.divergeAt]);

  const dataRef = React.useRef(fc); dataRef.current = fc;
  const modeRef = React.useRef(mode); modeRef.current = mode;
  const maxRef = React.useRef(maxV); maxRef.current = maxV;
  const regionRef = React.useRef(regionFC); regionRef.current = regionFC;
  const fmtRef = React.useRef(fmt); fmtRef.current = fmt;

  // Auto-zoom the map to fit all plotted regions so the view (and any PDF
  // export capture) is always well-framed.
  const fitToData = React.useCallback((map) => {
    try {
      let coords = [];
      if (modeRef.current === 'choropleth') {
        const gj = regionRef.current;
        if (gj && gj.features) {
          const withVal = gj.features.filter(f => f.properties && f.properties._val != null);
          (withVal.length ? withVal : gj.features).forEach(f => _collectPolyCoords(f.geometry, coords));
        }
      } else {
        coords = dataRef.current.features.map(f => f.geometry.coordinates);
      }
      if (!coords.length) return;
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const c of coords) { const x = c[0], y = c[1]; if (x < minX) minX = x; if (y < minY) minY = y; if (x > maxX) maxX = x; if (y > maxY) maxY = y; }
      if (!Number.isFinite(minX)) return;
      if (minX === maxX && minY === maxY) { map.easeTo({ center: [minX, minY], zoom: 5, duration: 0 }); return; }
      map.fitBounds([[minX, minY], [maxX, maxY]], { padding: 36, duration: 0, maxZoom: 7 });
    } catch (e) { /* noop */ }
  }, []);

  const addChoroplethOverlay = React.useCallback((map) => {
    try { if (map.getLayer('regions-fill')) map.removeLayer('regions-fill'); } catch { /* noop */ }
    try { if (map.getLayer('regions-line')) map.removeLayer('regions-line'); } catch { /* noop */ }
    try { if (map.getSource('regions')) map.removeSource('regions'); } catch { /* noop */ }
    const gj = regionRef.current;
    if (!gj || !gj.features || !gj.features.length) return;
    map.addSource('regions', { type: 'geojson', data: gj });
    map.addLayer({ id: 'regions-fill', type: 'fill', source: 'regions', paint: { 'fill-color': ['coalesce', ['get', '_fill'], 'rgba(148,163,184,0.12)'], 'fill-opacity': 0.82 } });
    map.addLayer({ id: 'regions-line', type: 'line', source: 'regions', paint: { 'line-color': 'rgba(255,255,255,0.45)', 'line-width': 0.4 } });
    fitToData(map);
  }, [fitToData]);

  const addOverlay = React.useCallback((map) => {
    try { if (map.getLayer('pts-heat')) map.removeLayer('pts-heat'); } catch { /* noop */ }
    try { if (map.getLayer('pts-circle')) map.removeLayer('pts-circle'); } catch { /* noop */ }
    try { if (map.getSource('pts')) map.removeSource('pts'); } catch { /* noop */ }
    const mx = maxRef.current || 1;
    map.addSource('pts', { type: 'geojson', data: dataRef.current });
    map.addLayer({
      id: 'pts-heat', type: 'heatmap', source: 'pts',
      layout: { visibility: modeRef.current === 'heatmap' ? 'visible' : 'none' },
      paint: {
        'heatmap-weight': ['interpolate', ['linear'], ['get', 'value'], 0, 0, mx, 1],
        'heatmap-intensity': 1.3,
        'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 0, 18, 6, 44],
        'heatmap-opacity': 0.85,
        'heatmap-color': ['interpolate', ['linear'], ['heatmap-density'],
          0, 'rgba(33,102,172,0)', 0.2, '#67a9cf', 0.4, '#d1e5f0', 0.6, '#fddbc7', 0.8, '#ef8a62', 1, '#b2182b'],
      },
    });
    map.addLayer({
      id: 'pts-circle', type: 'circle', source: 'pts',
      layout: { visibility: modeRef.current === 'points' ? 'visible' : 'none' },
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['get', 'value'], 0, 5, mx, 26],
        'circle-color': '#1464f4', 'circle-opacity': 0.75,
        'circle-stroke-color': '#ffffff', 'circle-stroke-width': 1,
      },
    });
    fitToData(map);
  }, [fitToData]);

  // Create (or recreate) the map when the basemap or scope changes.
  React.useEffect(() => {
    if (!containerRef.current) return undefined;
    let map;
    let cancelled = false;
    (async () => {
      // maplibre-gl v4 exposes a default export; be robust to either shape.
      const mod = await import('maplibre-gl');
      const maplibregl = mod.default || mod;
      await import('maplibre-gl/dist/maplibre-gl.css');
      if (cancelled || !containerRef.current) return;
      const view = MAPLIBRE_VIEW[scope] || MAPLIBRE_VIEW['us-states'];
      map = new maplibregl.Map({
        container: containerRef.current,
        style: MAPLIBRE_BASEMAPS[basemap] || MAPLIBRE_BASEMAPS.streets,
        center: view.center, zoom: view.zoom,
        attributionControl: { compact: true },
        preserveDrawingBuffer: true,
      });
      mapRef.current = map; readyRef.current = false;
      map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
      map.on('load', () => {
        readyRef.current = true;
        if (modeRef.current === 'choropleth') addChoroplethOverlay(map);
        else addOverlay(map);
        map.on('click', 'pts-circle', (e) => {
          const f = e.features && e.features[0];
          if (!f) return;
          new maplibregl.Popup()
            .setLngLat(e.lngLat)
            .setHTML(`<div style="font-weight:600">${_escapeHtml(f.properties.name)}</div><div>${_escapeHtml(fmtRef.current(f.properties.value))}</div>`)
            .addTo(map);
        });
        map.on('click', 'regions-fill', (e) => {
          const f = e.features && e.features[0];
          if (!f || f.properties._val == null) return;
          new maplibregl.Popup()
            .setLngLat(e.lngLat)
            .setHTML(`<div style="font-weight:600">${_escapeHtml(f.properties._label)}</div><div>${_escapeHtml(fmtRef.current(Number(f.properties._val)))}</div>`)
            .addTo(map);
        });
        map.on('mouseenter', 'pts-circle', () => { map.getCanvas().style.cursor = 'pointer'; });
        map.on('mouseleave', 'pts-circle', () => { map.getCanvas().style.cursor = ''; });
        map.on('mouseenter', 'regions-fill', () => { map.getCanvas().style.cursor = 'pointer'; });
        map.on('mouseleave', 'regions-fill', () => { map.getCanvas().style.cursor = ''; });
      });
    })();
    return () => { cancelled = true; readyRef.current = false; if (map) map.remove(); mapRef.current = null; };
  }, [basemap, scope, mode, addOverlay, addChoroplethOverlay]);

  // Update overlay data / layer visibility without recreating the map.
  React.useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (mode === 'choropleth') {
      if (!regionFC || !regionFC.features.length) return;
      const apply = () => {
        const src = map.getSource('regions');
        if (src) src.setData(regionFC); else addChoroplethOverlay(map);
        fitToData(map);
      };
      if (map.isStyleLoaded()) apply(); else map.once('idle', apply);
      return;
    }
    if (!readyRef.current) return;
    const src = map.getSource('pts');
    if (src) src.setData(fc);
    if (map.getLayer('pts-heat')) map.setLayoutProperty('pts-heat', 'visibility', mode === 'heatmap' ? 'visible' : 'none');
    if (map.getLayer('pts-circle')) map.setLayoutProperty('pts-circle', 'visibility', mode === 'points' ? 'visible' : 'none');
  }, [fc, regionFC, mode, addChoroplethOverlay, fitToData]);

  return (
    <div style={{ height, position: 'relative', borderRadius: 12, overflow: 'hidden' }} data-testid="maplibre-geo-chart">
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      {mode !== 'choropleth' && !fc.features.length && (
        <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 2, ...TT, padding: '6px 10px', maxWidth: 240 }}>
          No mappable rows for this result (unrecognised regions).
        </div>
      )}
    </div>
  );
}

// Chart type registry for the Report Builder
export const ADVANCED_CHART_TYPES = [
  { type: 'scatter', label: 'Scatter Plot', minDims: 0, minMsrs: 2 },
  { type: 'bubble', label: 'Bubble (3-measure scatter)', minDims: 0, minMsrs: 3 },
  { type: 'combo', label: 'Combo (Bar+Line)', minDims: 1, minMsrs: 2 },
  { type: 'gauge', label: 'Gauge', minDims: 0, minMsrs: 1 },
  { type: 'funnel', label: 'Funnel', minDims: 1, minMsrs: 1 },
  { type: 'waterfall', label: 'Waterfall', minDims: 1, minMsrs: 1 },
  { type: 'treemap', label: 'Treemap', minDims: 1, minMsrs: 1 },
  { type: 'heatmap', label: 'Heatmap', minDims: 2, minMsrs: 1 },
  { type: 'sankey', label: 'Sankey (Flow)', minDims: 2, minMsrs: 1 },
  { type: 'packed_bubbles', label: 'Packed Bubbles', minDims: 1, minMsrs: 1 },
  { type: 'geo_map', label: 'Geographic Map', minDims: 1, minMsrs: 1 },
];
